import React, { useMemo } from 'react'
import { faker } from '@faker-js/faker'

import { getCustomers } from './faker/customer'
import { getInvoicesForPayouts, type Invoice } from './faker/invoice'
import { type MonthlyDistribution, randomIntegerInRange } from './faker/utils'
import { useLayerApiUrl } from './ImportDataContext'
import {
  type BusinessIndustry,
  createCustomTransactions,
  createMatchingCustomTransactionsFromPayouts,
  type CustomAccount,
  getPayouts,
  ImportApiError,
  type PayoutParams,
  validatePayoutParamsList,
} from './importDummyDataHelpers'
import { ImportForm } from './ImportForm'
import { type ImportStepDef } from './useImportForm'

export interface ImportPayoutsProps {
  demoName: string
  businessId: string
  industry: BusinessIndustry
  businessAccessToken: string
  defaultCustomAccount: CustomAccount
  dist?: MonthlyDistribution
}

interface PayoutsData {
  invoices: Invoice[]
  payouts: PayoutParams[]
}

const buildPayoutSteps = (
  demoName: string,
  businessId: string,
  businessAccessToken: string,
  customAccountId: string,
  layerApiUrl: string,
) => (data: PayoutsData): ImportStepDef[] => {
  const customers = getCustomers(10, demoName)
  return [
    {
      label: 'Creating customers',
      run: async () => {
        const customerResponse = await fetch(
          `${layerApiUrl}/v1/businesses/${businessId}/customers/bulk`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${businessAccessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(customers),
          },
        )
        if (!customerResponse.ok) {
          throw new ImportApiError(
            `Customer creation API call failed: ${customerResponse.status} ${customerResponse.statusText}`,
            customerResponse.status,
          )
        }
      },
    },
    {
      label: 'Creating invoices',
      run: async () => {
        const invoicesWithCustomers = data.invoices.map(invoice => ({
          ...invoice,
          customer_external_id:
            customers[randomIntegerInRange(0, 9)].external_id,
        }))
        const invoiceResponse = await fetch(
          `${layerApiUrl}/v1/businesses/${businessId}/invoices/bulk`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${businessAccessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(invoicesWithCustomers),
          },
        )
        if (!invoiceResponse.ok) {
          throw new ImportApiError(
            `Invoice creation failed: ${invoiceResponse.status} ${invoiceResponse.statusText}`,
            invoiceResponse.status,
          )
        }
        await invoiceResponse.json()
      },
    },
    ...data.payouts.map((payout, i) => ({
      label: `Creating payout ${i + 1}/${data.payouts.length} (${payout.external_id})`,
      run: async () => {
        const payoutResponse = await fetch(
          `${layerApiUrl}/v1/businesses/${businessId}/payouts`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${businessAccessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payout),
          },
        )
        if (!payoutResponse.ok) {
          throw new ImportApiError(
            `Payout creation failed for payout ${payout.external_id}: ${payoutResponse.status} ${payoutResponse.statusText}`,
            payoutResponse.status,
          )
        }
        await payoutResponse.json()
        await new Promise(resolve => setTimeout(resolve, 1000))
      },
    })),
    {
      label: 'Creating matching transactions',
      run: async () => {
        const matchingTxns = createMatchingCustomTransactionsFromPayouts(
          data.payouts,
          demoName,
        )
        await createCustomTransactions(
          businessId,
          businessAccessToken,
          customAccountId,
          matchingTxns,
          layerApiUrl,
        )
      },
    },
  ]
}

export const ImportPayouts = ({
  demoName,
  businessId,
  businessAccessToken,
  defaultCustomAccount,
  dist,
}: ImportPayoutsProps) => {
  const layerApiUrl = useLayerApiUrl()
  const getSteps = useMemo(
    () => buildPayoutSteps(demoName, businessId, businessAccessToken, defaultCustomAccount.id, layerApiUrl),
    [demoName, businessId, businessAccessToken, defaultCustomAccount.id, layerApiUrl],
  )

  return (
    <ImportForm
      title='Payouts'
      defaultCustomAccount={defaultCustomAccount}
      getInitialData={() => {
        faker.seed(42)
        const invoices = getInvoicesForPayouts(demoName, dist)
        return {
          invoices,
          payouts: getPayouts(demoName, invoices),
        }
      }}
      validateData={data => validatePayoutParamsList(data.payouts)}
      getSteps={getSteps}
      dependencies={[demoName, dist]}
    />
  )
}
